export const INVOICE_DOCUMENT_TYPE_PREFIX = {
  invoice: "INV",
  tax_invoice: "TAX",
  quotation: "QUO",
  receipt: "REC",
  credit_note: "CRN",
  debit_note: "DBN",
  refund_receipt: "RFD",
  statement: "SOA",
};

export function getDocumentTypePrefix(documentType) {
  return INVOICE_DOCUMENT_TYPE_PREFIX[documentType] || "INV";
}

export function replaceDocumentTypePrefixInInvoiceNumber(
  invoiceNumber = "",
  documentType = "",
) {
  const normalized = String(invoiceNumber || "").trim();
  if (!normalized) return "";
  if (!documentType) return normalized;

  const nextPrefix = getDocumentTypePrefix(documentType);
  const match = normalized.match(/^([A-Za-z]+)(-.+)$/);
  if (!match) return normalized;

  return `${nextPrefix}${match[2]}`;
}

export function formatInvoiceNumber({ documentType, year, clientId }) {
  const prefix = getDocumentTypePrefix(documentType);
  const safeYear = year || new Date().getFullYear();
  if (!clientId) {
    return `${prefix}-${safeYear}-`;
  }
  return `${prefix}-${safeYear}-${clientId}`;
}

export function resolveInvoiceNumberFromRecord(invoice) {
  return String(
    invoice?.invoice_number ??
      invoice?.invoice_no ??
      invoice?.invoiceNo ??
      invoice?.number ??
      invoice?.invoiceNumber ??
      "",
  ).trim();
}

function collectTakenInvoiceNumbers(existingInvoices = [], excludeInvoiceId = null) {
  return new Set(
    (existingInvoices || [])
      .filter(
        (invoice) =>
          !excludeInvoiceId || String(invoice?.id) !== String(excludeInvoiceId),
      )
      .map((invoice) => resolveInvoiceNumberFromRecord(invoice).toUpperCase())
      .filter(Boolean),
  );
}

export function buildUniqueInvoiceNumber({
  documentType,
  clientId,
  year,
  existingInvoices = [],
  excludeInvoiceId = null,
}) {
  const base = formatInvoiceNumber({
    documentType,
    year: year || new Date().getFullYear(),
    clientId,
  });

  if (!base) return "";

  const taken = collectTakenInvoiceNumbers(existingInvoices, excludeInvoiceId);
  const baseKey = base.toUpperCase();

  if (!taken.has(baseKey)) {
    return base;
  }

  const escapedBase = base.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const suffixPattern = new RegExp(`^${escapedBase}-(\\d+)$`, "i");
  let maxSuffix = 1;

  taken.forEach((number) => {
    const match = number.match(suffixPattern);
    if (match) {
      maxSuffix = Math.max(maxSuffix, parseInt(match[1], 10));
    }
  });

  let candidateSuffix = maxSuffix + 1;
  while (candidateSuffix < 10000) {
    const candidate = `${base}-${candidateSuffix}`;
    if (!taken.has(candidate.toUpperCase())) {
      return candidate;
    }
    candidateSuffix += 1;
  }

  return `${base}-${Date.now()}`;
}

export function incrementInvoiceNumber(current = "") {
  const normalized = String(current || "").trim();
  if (!normalized) return "";

  const suffixMatch = normalized.match(/^(.*)-(\d+)$/);
  if (suffixMatch) {
    return `${suffixMatch[1]}-${parseInt(suffixMatch[2], 10) + 1}`;
  }

  return `${normalized}-2`;
}

export function mergeInvoiceRecordsForNumbering(...lists) {
  const byId = new Map();

  lists.flat().forEach((invoice) => {
    if (!invoice || typeof invoice !== "object") return;

    const id = invoice.id ?? invoice.invoice_id ?? invoice.invoiceId;
    const number = resolveInvoiceNumberFromRecord(invoice);

    if (id !== null && id !== undefined && id !== "") {
      byId.set(String(id), invoice);
      return;
    }

    if (number) {
      byId.set(number.toUpperCase(), invoice);
    }
  });

  return Array.from(byId.values());
}

export function buildDraftInvoiceNumber({
  documentType,
  clientId,
  year,
  existingInvoices = [],
  excludeInvoiceId = null,
}) {
  if (!documentType) return "";

  return buildUniqueInvoiceNumber({
    documentType,
    clientId,
    year,
    existingInvoices,
    excludeInvoiceId,
  });
}

export function extractNextInvoiceNumberFromResponse(response) {
  const payload = response?.data?.data ?? response?.data;

  if (payload === null || payload === undefined) {
    return "";
  }

  if (typeof payload === "number" || typeof payload === "string") {
    return String(payload).trim();
  }

  if (typeof payload !== "object") {
    return "";
  }

  const candidates = [
    payload.invoice_number,
    payload.next_invoice_number,
    payload.nextInvoiceNumber,
    payload.number,
    payload.next_number,
    payload.sequence,
  ];

  for (const value of candidates) {
    if (value !== null && value !== undefined && String(value).trim() !== "") {
      return String(value).trim();
    }
  }

  return "";
}

export function parseBackendInvoiceNumberTemplate(invoiceNumberTemplate = "") {
  const normalized = String(invoiceNumberTemplate || "").trim();
  if (!normalized) {
    return { prefix: "INV", sequence: "" };
  }

  const doubleDashMatch = normalized.match(/^([A-Za-z]+)--(.+)$/);
  if (doubleDashMatch) {
    return {
      prefix: doubleDashMatch[1].toUpperCase(),
      sequence: doubleDashMatch[2],
    };
  }

  const withClientMatch = normalized.match(/^([A-Za-z]+)-([^-]+)-(.+)$/);
  if (withClientMatch) {
    return {
      prefix: withClientMatch[1].toUpperCase(),
      sequence: withClientMatch[3],
    };
  }

  const prefixSequenceMatch = normalized.match(/^([A-Za-z]+)-(.+)$/);
  if (prefixSequenceMatch) {
    return {
      prefix: prefixSequenceMatch[1].toUpperCase(),
      sequence: prefixSequenceMatch[2],
    };
  }

  return { prefix: "INV", sequence: normalized };
}

export function buildInvoiceNumberWithClientId({
  invoiceNumberTemplate = "",
  clientId,
  documentType = "",
}) {
  const template = String(invoiceNumberTemplate || "").trim();
  if (!template) return "";

  const parsed = parseBackendInvoiceNumberTemplate(template);
  const prefix = documentType
    ? getDocumentTypePrefix(documentType)
    : parsed.prefix || "INV";
  const safeClientId = String(clientId || "").trim();
  const sequence = String(parsed.sequence || "").trim();

  if (!sequence) return "";
  if (!safeClientId) return `${prefix}--${sequence}`;
  return `${prefix}-${safeClientId}-${sequence}`;
}

export function buildInvoiceNumberFromNextSequence({
  documentType = "invoice",
  userId,
  sequence,
}) {
  return buildInvoiceNumberWithClientId({
    invoiceNumberTemplate: sequence,
    clientId: userId,
    documentType,
  });
}

export async function fetchNextInvoiceSequence(apiClient) {
  const response = await apiClient.get("getNextInvoiceNumber");
  return extractNextInvoiceNumberFromResponse(response);
}
